<template>
    <div class="row">
        <div class="col-12 col-md-12 note-head">
            <div class="note-title">
                <h4>Dentition Plan</h4>
            </div>
        </div>
        <div class="col-12">
            <hr class="mt-0">
        </div>

        <div class="col-12 col-md-12 note-body">
            <div class="card card-body">
                <div class="arch-chart">
                    <div class="patient-sides">
                        <span>← Patient's Right</span>
                        <span>Patient's Left →</span>
                    </div>

                    <div class="arch-label maxilla-label">MAXILLA (Upper Jaw)</div>

                    <ToothArchRow
                        v-for="(row, rowIndex) in maxillaRows" :key="`max-${rowIndex}`"
                        :row="row"
                        :get-tooth-class="getToothClass"
                        @select="onSelectDentition"
                    />

                    <div class="occlusal-plane"><span>— Occlusal Plane (Maxilla | Mandible) —</span></div>

                    <ToothArchRow
                        v-for="(row, rowIndex) in mandibleRows" :key="`man-${rowIndex}`"
                        :row="row"
                        :get-tooth-class="getToothClass"
                        @select="onSelectDentition"
                    />

                    <div class="arch-label mandible-label">MANDIBLE (Lower Jaw)</div>
                </div>
            </div>
        </div>

        <BillingDetailModal
            v-if="isShowBillingDetail"
            v-model:isShowBillingDetail="isShowBillingDetail"
            :tableItems="tableItems"
        />
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import BillingDetailModal from '@/components/molecule/company/dental-os/service/middle-header/BillingDetailModal.vue';
import ToothArchRow from './ToothArchRow.vue';

const props = defineProps({
    dentition: {
        type: Object || null,
        default: () => {}
    },
    billedDentition: {
        type: Object || null,
        default: () => {}
    },
});

const isShowBillingDetail = ref(false);
const tableItems = ref({});
const selectedDentition = ref([]);

const isFdi = computed(() => props.dentition && props.dentition.notation_type === 'fdi');
const dentitionPrefix = computed(() => isFdi.value ? 'fdi_' : '');

const permanentSet = computed(() => {
    if (! props.dentition) return {};
    return props.dentition[dentitionPrefix.value + 'permanent_dentition'] || {};
});

const primarySet = computed(() => {
    if (! props.dentition) return {};
    return props.dentition[dentitionPrefix.value + 'primary_dentition'] || {};
});

const toothCodeMap = computed(() => (props.dentition && props.dentition.tooth_code_map) || {});

const getDentitionCode = (value) => {
    return toothCodeMap.value[value] || value;
}

const buildHalf = (values) => {
    return values.map(function (value) {
        return {
            value: value,
            code: getDentitionCode(value)
        };
    });
}

const orderForMandible = (values, reverse) => {
    if (! reverse) return values;
    return values.slice().reverse();
}

const buildRow = (type, leftValues, rightValues, labels = {}) => {
    return {
        type: type,
        left: buildHalf(leftValues),
        right: buildHalf(rightValues),
        leftLabel: labels.left || '',
        rightLabel: labels.right || ''
    };
}

const splitInHalf = (values) => {
    const mid = Math.ceil(values.length / 2);
    return [values.slice(0, mid), values.slice(mid)];
}

const permanentRow = (leftKey, rightKey, reverse = false, labels = {}) => {
    const left = orderForMandible(permanentSet.value[leftKey] || [], reverse);
    const right = orderForMandible(permanentSet.value[rightKey] || [], reverse);
    return buildRow('permanent', left, right, labels);
}

const fdiPrimaryRow = (leftKey, rightKey, reverse = false, labels = {}) => {
    const left = orderForMandible(primarySet.value[leftKey] || [], reverse);
    const right = orderForMandible(primarySet.value[rightKey] || [], reverse);
    return buildRow('primary', left, right, labels);
}

const universalPrimaryRow = (sourceKey) => {
    const halves = splitInHalf(primarySet.value[sourceKey] || []);
    return buildRow('primary', halves[0], halves[1]);
}

const maxillaRows = computed(() => {
    if (isFdi.value) {
        return [
            permanentRow('top_start', 'top_end', false, { left: 'Q1', right: 'Q2' }),
            fdiPrimaryRow('top_start', 'top_end', false, { left: 'Q5', right: 'Q6' })
        ];
    }
    return [permanentRow('top_start', 'top_end'), universalPrimaryRow('top_start')];
});

const mandibleRows = computed(() => {
    if (isFdi.value) {
        return [
            fdiPrimaryRow('bottom_start', 'bottom_end', true, { left: 'Q8', right: 'Q7' }),
            permanentRow('bottom_start', 'bottom_end', true, { left: 'Q4', right: 'Q3' })
        ];
    }
    return [universalPrimaryRow('top_end'), permanentRow('bottom_start', 'bottom_end')];
});

const onSelectDentition = (toothCode) => {
    tableItems.value = props.billedDentition[toothCode] || {};
    isShowBillingDetail.value = true;
}

const getToothClass = (value, type) => {
    const isSelected = selectedDentition.value.includes(value);
    if (type === 'primary') {
        return isSelected ? 'btn-success' : 'btn-outline-success';
    }
    return isSelected ? 'btn-primary' : 'btn-outline-primary';
}

watch(() => props.billedDentition, (newVal) => {
    selectedDentition.value = Object.keys(newVal || {});
}, { deep: true, immediate: true })
</script>

<style scoped>
.note-title {
    min-width: 200px;
}
.note-head {
    display: flex;
    justify-content: space-between;
    margin-bottom: 1%;
}
.note-body .card {
    background: #ffffff;
    min-height: 100px;
    margin-bottom: 1rem;
}
.note-body .card-body {
    padding: 0 2%;
}
.arch-chart {
    padding: 10px 16px 0;
}
.patient-sides {
    display: flex;
    justify-content: space-between;
    color: #6b7280;
    font-size: 0.72rem;
    margin-bottom: 2px;
}
.arch-label {
    text-align: center;
    font-weight: 700;
    color: #2d4ea2;
    letter-spacing: 0.04em;
    margin: 6px 0;
}
.occlusal-plane {
    text-align: center;
    margin: 14px 0;
    border-top: 1px solid #e2e6ee;
    line-height: 0.1em;
}
.occlusal-plane span {
    background: #fff;
    padding: 0 12px;
    color: #9aa3b2;
    font-size: 0.75rem;
}
</style>
